// /var/www/afra/web-design.rampagesoft.com/my-app/src/component/BannerHome.js
import React, { useState, useEffect, useRef } from 'react';
import bannerData from '../data/bannerData.json';
import styles from '../css/BannerHome.module.scss';

const CACHE_NAME = 'banner-video-cache-v3';

const HomeBanner = () => {
  const { title, description, link, video, poster } = bannerData[0] || {};
  const defaultVideo = 'https://afaa.website/s/default-video.mp4'; // Fallback video
  const defaultPoster = 'https://afaa.website/s/ff6970.png'; // Fallback poster
  const videoUrl = video && video !== 'undefined' ? video.replace(/^\./, '') : defaultVideo;
  const posterUrl = poster && poster !== 'undefined' ? poster.replace(/^\./, '') : defaultPoster;
  const [src, setSrc] = useState(videoUrl);
  const [videoReady, setVideoReady] = useState(false);
  const [error, setError] = useState(null);
  const objectUrlRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    let didCancel = false;

    const cacheVideo = async () => {
      try {
        if ('caches' in window) {
          const cache = await caches.open(CACHE_NAME);
          const cachedResponse = await cache.match(videoUrl);

          if (cachedResponse) {
            // Load from cache
            const blob = await cachedResponse.blob();
            const objUrl = URL.createObjectURL(blob);
            objectUrlRef.current = objUrl;
            if (!didCancel) setSrc(objUrl);
          } else {
            // Fetch and cache
            const response = await fetch(videoUrl, { method: 'GET', mode: 'cors' });
            if (!response.ok) {
              throw new Error(`Failed to fetch video: ${response.status} ${response.statusText}`);
            }
            await cache.put(videoUrl, response.clone());
            // Keep streaming from videoUrl
            if (!didCancel) setSrc(videoUrl);
          }
        } else {
          // Fallback to direct URL
          if (!didCancel) setSrc(videoUrl);
        }
      } catch (err) {
        console.error('Video load error:', err.message);
        if (!didCancel) setError(err.message);
      }
    };

    cacheVideo();

    return () => {
      didCancel = true;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [videoUrl]);

  // Handle video metadata loaded
  const handleLoadedMetadata = () => {
    setVideoReady(true);
    if (videoRef.current) {
      videoRef.current.play().catch((err) => {
        console.error('Video play error:', err.message);
        setError('ไม่สามารถเล่นวิดีโอได้');
      });
    }
  };

  // Handle video error
  const handleVideoError = (e) => {
    console.error('Video playback error:', e.message || 'Unknown error');
    setError('ไม่สามารถโหลดวิดีโอได้');
  };

  // Handle poster error
  const handlePosterError = () => {
    console.error('Failed to load poster:', posterUrl);
    setError('ไม่สามารถโหลดภาพโปสเตอร์ได้');
  };

  return (
    <div className={styles.homeBanner}>
      {error ? (
        <div className={styles.fallbackContainer}>
          <img
            src={posterUrl}
            alt="บริการออกแบบเว็บไซต์ ดีไซน์เว็บไซต์ Future Technologies RAMPAGESOFT"
            className={styles.bannerVideo}
            onError={handlePosterError}
          />
          {error && (
            <p className={styles.errorMessage} role="alert">
              {error}
            </p>
          )}
        </div>
      ) : (
        <>
          {!videoReady && (
            <img
              src={posterUrl}
              alt="บริการออกแบบเว็บไซต์ ดีไซน์เว็บไซต์ Future Technologies RAMPAGESOFT"
              className={styles.bannerVideo}
              onError={handlePosterError}
            />
          )}
          <video
            ref={videoRef}
            className={styles.bannerVideo}
            src={src}
            poster={posterUrl}
            muted
            loop
            playsInline
            preload="metadata"
            loading="lazy"
            onLoadedMetadata={handleLoadedMetadata}
            onError={handleVideoError}
            style={{ opacity: videoReady ? 1 : 0, transition: 'opacity 0.3s ease' }}
            aria-label="วิดีโอ บริการออกแบบเว็บไซต์ ดีไซน์เว็บไซต์ Future Technologies RAMPAGESOFT"
          />
        </>
      )}

      <div
        className={`${styles.bannerOverlay} ${styles.animateFadeIn} ${styles.animateFadeInDelayed}`}
        aria-live="polite"
      >
        <h2>{title || 'Default Title'}</h2>
        <p>{description || 'Default Description'}</p>
        <a
          href={link || '#'}
          className={styles.bannerButton}
          aria-label="ไปที่หน้าเว็บไซต์ดีไซน์"
        >
          ไปที่หน้าเว็บไซต์ดีไซน์
        </a>
      </div>
    </div>
  );
};

export default HomeBanner;